import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Bus, ArrowLeft, MapPin, Clock, Navigation, 
  CheckCircle, Circle, AlertCircle, RefreshCw
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusTracking = () => {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [tripId]);

  const fetchInitialData = async () => {
    try {
      const response = await api.get(`/bus/tracking/${tripId}`);
      setTracking(response.data);
    } catch (error) {
      console.error('Error fetching tracking:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tracking data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL
      ?.replace('https://', 'wss://')
      ?.replace('http://', 'ws://');
    
    if (!wsUrl) return;

    try {
      wsRef.current = new WebSocket(`${wsUrl}/ws/bus/track/${tripId}`);
      
      wsRef.current.onopen = () => {
        setWsConnected(true);
        console.log('Tracking WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleTrackingUpdate(data);
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        // Reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  };

  const handleTrackingUpdate = (data) => {
    switch (data.type) {
      case 'initial_state':
      case 'location_update':
        setTracking(prev => ({
          ...prev,
          currentLocation: data.location || data.currentLocation,
          stopsEta: data.stopsEta || prev?.stopsEta,
          stopsReached: data.stopsReached || prev?.stopsReached
        }));
        break;
      case 'stop_reached':
        toast({
          title: 'Stop Reached',
          description: data.message
        });
        fetchInitialData(); // Refresh full data
        break;
      case 'trip_started':
        toast({
          title: 'Trip Started!',
          description: data.message
        });
        setTracking(prev => ({
          ...prev,
          trackingStatus: 'active'
        }));
        break;
      case 'trip_completed':
        toast({
          title: 'Trip Completed',
          description: data.message
        });
        setTracking(prev => ({
          ...prev,
          trackingStatus: 'ended'
        }));
        break;
      default:
        break;
    }
  };

  const handleRefresh = () => {
    fetchInitialData();
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send('refresh');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const getStopStatus = (stopIndex) => {
    const reached = tracking?.stopsReached?.find(s => s.stopIndex === stopIndex);
    return reached ? 'reached' : 'pending';
  };

  const formatEta = (minutes) => {
    if (!minutes) return '--';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} min`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/bus/tickets')}>
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-xl font-bold">Track Your Bus</h1>
                <p className="text-blue-100 text-sm">
                  {tracking?.route?.origin?.city} → {tracking?.route?.destination?.city}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {wsConnected ? (
                <span className="flex items-center gap-1 text-xs bg-green-500/20 text-green-100 px-2 py-1 rounded-full">
                  <Circle className="h-2 w-2 fill-green-400" />
                  Live
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs bg-yellow-500/20 text-yellow-100 px-2 py-1 rounded-full">
                  <Circle className="h-2 w-2 fill-yellow-400" />
                  Connecting...
                </span>
              )}
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-white"
                onClick={handleRefresh}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Tracking Status */}
        <Card className="p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-full ${
                tracking?.trackingStatus === 'active' 
                  ? 'bg-green-100' 
                  : tracking?.trackingStatus === 'ended'
                    ? 'bg-gray-100'
                    : 'bg-yellow-100'
              }`}>
                <Bus className={`h-6 w-6 ${
                  tracking?.trackingStatus === 'active' 
                    ? 'text-green-600' 
                    : tracking?.trackingStatus === 'ended'
                      ? 'text-gray-600'
                      : 'text-yellow-600'
                }`} />
              </div>
              <div>
                <h3 className="font-semibold">
                  {tracking?.trackingStatus === 'active' 
                    ? 'Bus is On The Way!' 
                    : tracking?.trackingStatus === 'ended'
                      ? 'Trip Completed'
                      : 'Waiting to Depart'
                  }
                </h3>
                <p className="text-sm text-gray-500">
                  {tracking?.trackingStatus === 'active' && tracking?.currentLocation
                    ? `Speed: ${tracking.currentLocation.speedKmh || 0} km/h`
                    : tracking?.trackingStatus === 'ended'
                      ? 'Thank you for traveling with Tekstar!'
                      : 'Tracking will start when the bus departs'
                  }
                </p>
              </div>
            </div>
            
            {tracking?.currentLocation && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Last updated</p>
                <p className="text-sm font-medium">
                  {new Date(tracking.currentLocation.updatedAt).toLocaleTimeString()}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Map Placeholder */}
        <Card className="p-4 mb-4">
          <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>Live map tracking</p>
              <p className="text-sm">
                {tracking?.currentLocation 
                  ? `${tracking.currentLocation.latitude?.toFixed(4)}, ${tracking.currentLocation.longitude?.toFixed(4)}`
                  : 'Waiting for GPS data...'
                }
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            In production, this would show a Google Maps/Mapbox view with real-time bus position
          </p>
        </Card>

        {/* Route Progress */}
        <Card className="p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Navigation className="h-5 w-5 text-blue-600" />
            Route Progress
          </h3>
          
          <div className="relative">
            {/* Origin */}
            <div className="flex items-start gap-4 mb-4">
              <div className="flex flex-col items-center">
                <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                  <CheckCircle className="h-3 w-3 text-white" />
                </div>
                <div className="w-0.5 h-8 bg-gray-200" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{tracking?.route?.origin?.city}</p>
                <p className="text-sm text-gray-500">{tracking?.route?.origin?.station}</p>
                <p className="text-xs text-green-600">Departed</p>
              </div>
            </div>

            {/* Stops */}
            {tracking?.route?.stops?.map((stop, idx) => {
              const status = getStopStatus(idx);
              const eta = tracking?.stopsEta?.find(s => s.index === idx);
              
              return (
                <div key={idx} className="flex items-start gap-4 mb-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                      status === 'reached' 
                        ? 'bg-green-500' 
                        : 'bg-gray-200'
                    }`}>
                      {status === 'reached' ? (
                        <CheckCircle className="h-3 w-3 text-white" />
                      ) : (
                        <Circle className="h-2 w-2 text-gray-400" />
                      )}
                    </div>
                    <div className="w-0.5 h-8 bg-gray-200" />
                  </div>
                  <div className="flex-1 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{stop.city}</p>
                      <p className="text-sm text-gray-500">{stop.station}</p>
                    </div>
                    <div className="text-right">
                      {status === 'reached' ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          Reached
                        </span>
                      ) : eta?.eta_minutes ? (
                        <div>
                          <p className="text-sm font-medium text-blue-600">
                            {formatEta(eta.eta_minutes)}
                          </p>
                          <p className="text-xs text-gray-400">ETA</p>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">--</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Destination */}
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                  tracking?.trackingStatus === 'ended'
                    ? 'bg-green-500'
                    : 'bg-blue-500'
                }`}>
                  {tracking?.trackingStatus === 'ended' ? (
                    <CheckCircle className="h-3 w-3 text-white" />
                  ) : (
                    <MapPin className="h-3 w-3 text-white" />
                  )}
                </div>
              </div>
              <div className="flex-1 flex items-center justify-between">
                <div>
                  <p className="font-medium">{tracking?.route?.destination?.city}</p>
                  <p className="text-sm text-gray-500">{tracking?.route?.destination?.station}</p>
                </div>
                <div className="text-right">
                  {tracking?.trackingStatus === 'ended' ? (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      Arrived
                    </span>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-blue-600">Final Stop</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Info */}
        <Card className="mt-4 p-4 bg-blue-50 border-blue-100">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">About Bus Tracking</p>
              <p className="mt-1">
                Location updates every 30 seconds. ETAs are estimates and may 
                vary based on traffic and road conditions.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BusTracking;
